import { CircleCheck, CircleSlash } from "lucide-react";
import { AdminDataNotice } from "@/components/admin/data-notice";
import { RefreshButton } from "@/components/admin/refresh-button";
import { useAdminView } from "@shared/client/admin-view";
import { Link } from "@shared/client/navigation";
import { usePageTitle } from "@shared/client/view";
import { AdminChip, AdminEmpty, AdminMeta, AdminPanel, PanelLink, StatCard, StatGroup } from "@shared/components/blocks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import type { AgentTraceSummary } from "@shared/lib/admin-db";
import { SOURCE_GROUP_LABELS, sourceChannels, type SourceGroupId } from "@shared/lib/data-sources";
import { formatDateTime, formatNumber } from "@shared/lib/format";
import type { LinkedinLookupStats } from "@shared/lib/linkedin-profile";
import type { sourceBackendHealth } from "@shared/lib/source-connectors";
import { AGENT_TRACE_KIND_LABELS, AGENT_TRACE_PROVIDER_LABELS } from "./agent-labels";

const SCOPE_LABELS: Record<string, string> = { fr: "France", eu: "Europe", global: "International" };

type SourcesData = {
  backends: ReturnType<typeof sourceBackendHealth>;
  linkedin: LinkedinLookupStats;
  traces: AgentTraceSummary[];
};

const percent = (part: number, total: number) => (total > 0 ? `${Math.round((part / total) * 100)} %` : "—");
const dollars = (value: number) => `${value.toLocaleString("fr-FR", { maximumFractionDigits: 2, minimumFractionDigits: 2 })} $`;

/**
 * Le taux de profils LinkedIn trouvés par Exa, par version de la recherche :
 * c'est lui qu'on suit quand on retouche la requête ou le filtre.
 */
function LinkedinLookups({ stats }: { stats: LinkedinLookupStats }) {
  const { recent, coverage, versions } = stats;
  return (
    <AdminPanel className="mb-4.5" flush title="Profils LinkedIn des interlocuteurs">
      <StatGroup bordered={false} columns={4}>
        <StatCard
          label="Profils trouvés par Exa (30 j)"
          note={`${formatNumber(recent.found)} sur ${formatNumber(recent.lookups)} recherches`}
          value={percent(recent.found, recent.lookups)}
        />
        <StatCard
          label="Coût par profil trouvé (30 j)"
          note={`${dollars(recent.cost_dollars)} au total`}
          value={recent.found > 0 ? dollars(recent.cost_dollars / recent.found) : "—"}
        />
        <StatCard
          label="Interlocuteurs avec LinkedIn (30 j)"
          note={`${formatNumber(coverage.with_linkedin)} sur ${formatNumber(coverage.contacts)}, toutes origines`}
          value={percent(coverage.with_linkedin, coverage.contacts)}
        />
        <StatCard
          label="Interlocuteurs avec photo (30 j)"
          note={`${formatNumber(coverage.with_photo)} sur ${formatNumber(coverage.contacts)}`}
          value={percent(coverage.with_photo, coverage.contacts)}
        />
      </StatGroup>
      {versions.length === 0 ? (
        <AdminEmpty>Aucune recherche de profil pour l’instant : elles commencent avec la clé Exa.</AdminEmpty>
      ) : (
        <Table className="border-t">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5">Version de la recherche</TableHead>
              <TableHead>Trouvés</TableHead>
              <TableHead className="max-sm:hidden">Recherches</TableHead>
              <TableHead className="max-sm:hidden">Avec photo</TableHead>
              <TableHead className="max-sm:hidden">Erreurs</TableHead>
              <TableHead className="max-sm:hidden">Coût</TableHead>
              <TableHead className="pr-5 max-sm:hidden">Période</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versions.map((row) => (
              <TableRow key={row.version}>
                <TableCell className="pl-5 max-sm:pr-4">
                  <strong className="font-semibold">v{row.version}</strong>
                  {row.current && <span className="ml-2 text-[12px] text-muted-foreground">en vigueur</span>}
                  {row.label && <small className="block max-w-[360px] text-[12px] leading-snug text-muted-foreground">{row.label}</small>}
                </TableCell>
                <TableCell className="whitespace-nowrap tabular-nums">
                  <strong className="font-semibold">{percent(row.found, row.lookups)}</strong>
                  <small className="block text-[12px] text-muted-foreground">{formatNumber(row.found)} profils</small>
                </TableCell>
                <TableCell className="tabular-nums max-sm:hidden">{formatNumber(row.lookups)}</TableCell>
                <TableCell className="tabular-nums max-sm:hidden">{percent(row.photos, row.found)}</TableCell>
                <TableCell className="tabular-nums max-sm:hidden">{formatNumber(row.errors)}</TableCell>
                <TableCell className="whitespace-nowrap tabular-nums max-sm:hidden">{dollars(row.cost_dollars)}</TableCell>
                <TableCell className="pr-5 whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
                  {formatDateTime(row.first_at)} → {formatDateTime(row.last_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <p className="m-0 border-t px-5 py-3.5 text-[12px] leading-relaxed text-muted-foreground">
        Une ligne par version de la recherche (<code className="font-mono">LINKEDIN_LOOKUP_VERSION</code> dans{" "}
        <code className="font-mono">shared/lib/linkedin-profile.ts</code>) : chaque retouche de la requête ou du filtre en
        ajoute une, et un test l’exige. Un profil n’est retenu que s’il porte le nom et l’organisation, et s’il est le seul.
      </p>
    </AdminPanel>
  );
}

export default function AdminSourcesPage() {
  usePageTitle("Sources de données — Administration Hune");
  const { data, error: loadError } = useAdminView<SourcesData>("/api/views/admin/sources");
  if (!data && !loadError) return null;
  const backends = data?.backends ?? [];
  const traces = data?.traces ?? [];

  const searchBackends = backends.filter((backend) => backend.role === "search");
  const availableSearch = searchBackends.filter((backend) => backend.available).length;
  const errorCount = traces.filter((trace) => trace.status === "error").length;
  const families = new Set(sourceChannels.flatMap((channel) => channel.families));
  const groups = Object.keys(SOURCE_GROUP_LABELS) as SourceGroupId[];

  return (
    <>
      <AdminMeta note={<RefreshButton />}>
        Ce que les agents peuvent interroger : {sourceChannels.length} canaux nommés, exposés à l’agent de recherche
        via l’outil <code className="font-mono">search_data_sources</code> et lus par{" "}
        <code className="font-mono">read_page</code>.
      </AdminMeta>

      <StatGroup className="mb-4.5" columns={4}>
        <StatCard label="Canaux catalogués" value={formatNumber(sourceChannels.length)} />
        <StatCard label="Familles de signaux couvertes" tint="teal" value={formatNumber(families.size)} />
        <StatCard
          label="Backends de recherche actifs"
          tint={availableSearch === 0 ? "rose" : "green"}
          value={`${availableSearch} / ${searchBackends.length}`}
        />
        <StatCard
          label="Appels en erreur (60 derniers)"
          tint={errorCount > 0 ? "rose" : "blue"}
          value={formatNumber(errorCount)}
        />
      </StatGroup>

      <AdminPanel className="mb-4.5" flush title="Backends disponibles">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5">Backend</TableHead>
              <TableHead className="max-sm:hidden">Rôle</TableHead>
              <TableHead className="max-sm:hidden">Ordre</TableHead>
              <TableHead className="max-sm:pr-4">État</TableHead>
              <TableHead className="pr-5 max-sm:hidden">Détail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {backends.map((backend) => (
              <TableRow key={`${backend.role}-${backend.id}`}>
                <TableCell className="pl-5">
                  <strong className="font-semibold">{backend.label}</strong>
                  {backend.envVar && (
                    <small className="block text-[12px] text-muted-foreground">
                      <code className="font-mono">{backend.envVar}</code>
                    </small>
                  )}
                </TableCell>
                <TableCell className="max-sm:hidden">{backend.role === "search" ? "Recherche" : "Lecture"}</TableCell>
                <TableCell className="max-sm:hidden">{backend.rank === 1 ? "Principal" : "Repli"}</TableCell>
                <TableCell className="max-sm:pr-4">
                  <AdminChip className="gap-1.5" tone={backend.available ? "active" : "neutral"}>
                    {backend.available ? <CircleCheck className="size-3" /> : <CircleSlash className="size-3" />}
                    {backend.available ? "Disponible" : "Non configuré"}
                  </AdminChip>
                </TableCell>
                <TableCell className="pr-5 text-muted-foreground max-sm:hidden">{backend.detail}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="m-0 border-t px-5 py-3.5 text-[12px] leading-relaxed text-muted-foreground">
          Les clés se posent dans l’environnement de déploiement. Sans backend de recherche,
          l’outil <code className="font-mono">search_data_sources</code> n’est pas exposé à l’agent : la recherche web
          générale et la lecture de page restent actives.
        </p>
      </AdminPanel>

      {data?.linkedin && <LinkedinLookups stats={data.linkedin} />}

      <AdminPanel className="mb-4.5" flush title="Catalogue des canaux">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5 max-sm:pr-4">Canal</TableHead>
              <TableHead className="max-sm:hidden">Zone</TableHead>
              <TableHead className="max-sm:hidden">Ce qu’on y trouve</TableHead>
              <TableHead className="max-sm:hidden">Plateformes</TableHead>
              <TableHead className="pr-5 max-sm:hidden">Familles</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.flatMap((group) => {
              const channels = sourceChannels.filter((channel) => channel.group === group);
              if (channels.length === 0) return [];
              return [
                <TableRow className="bg-muted/60 hover:bg-muted/60" key={group}>
                  <TableCell className="pl-5 text-[12px] font-medium" colSpan={5}>
                    {SOURCE_GROUP_LABELS[group]}
                  </TableCell>
                </TableRow>,
                ...channels.map((channel) => (
                  <TableRow key={channel.id}>
                    <TableCell className="pl-5 max-sm:pr-4">
                      <strong className="font-semibold">{channel.label}</strong>
                      <small className="block text-[12px] text-muted-foreground">
                        <code className="font-mono">{channel.id}</code>
                      </small>
                      {!channel.readable && (
                        <small className="block text-[12px] text-muted-foreground">
                          Recherche seule (pages derrière un compte)
                        </small>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap max-sm:hidden">{SCOPE_LABELS[channel.scope] ?? channel.scope}</TableCell>
                    <TableCell className="text-muted-foreground max-sm:hidden">{channel.yields}</TableCell>
                    <TableCell className="text-muted-foreground max-sm:hidden">
                      {channel.domains.length === 0 ? "Web ouvert" : channel.domains.join(", ")}
                    </TableCell>
                    <TableCell className="pr-5 text-muted-foreground max-sm:hidden">{channel.families.join(", ")}</TableCell>
                  </TableRow>
                )),
              ];
            })}
          </TableBody>
        </Table>
      </AdminPanel>

      <AdminPanel
        action={<PanelLink href="/admin/agents?kind=source_search">Toutes les traces</PanelLink>}
        flush
        title="Derniers appels aux sources"
      >
        {loadError ? (
          <div className="p-4"><AdminDataNotice error={loadError} /></div>
        ) : traces.length === 0 ? (
          <AdminEmpty>Aucun appel aux canaux de données pour l’instant.</AdminEmpty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Quand</TableHead>
                <TableHead className="max-sm:hidden">Type</TableHead>
                <TableHead>Backend</TableHead>
                <TableHead className="max-sm:pr-4">Statut</TableHead>
                <TableHead className="max-sm:hidden">Durée</TableHead>
                <TableHead className="pr-5 max-sm:hidden">Détail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {traces.map((trace) => (
                <TableRow className={trace.status === "error" ? "bg-destructive/4" : undefined} key={trace.id}>
                  <TableCell className="pl-5 whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatDateTime(trace.created_at)}
                  </TableCell>
                  <TableCell className="max-sm:hidden">{AGENT_TRACE_KIND_LABELS[trace.kind] ?? trace.kind}</TableCell>
                  <TableCell>{AGENT_TRACE_PROVIDER_LABELS[trace.provider] ?? trace.provider}</TableCell>
                  <TableCell className="max-sm:pr-4">
                    <AdminChip tone={trace.status === "error" ? "churned" : "active"}>
                      {trace.status === "error" ? "Erreur" : "Succès"}
                    </AdminChip>
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums max-sm:hidden">
                    {trace.duration_ms === null ? "—" : `${formatNumber(trace.duration_ms)} ms`}
                  </TableCell>
                  <TableCell className="pr-5 max-sm:hidden">
                    <Link className="font-semibold text-primary hover:underline" href={`/admin/agents/${trace.id}`}>
                      Voir la trace
                    </Link>
                    {trace.error && <small className="block text-[12px] text-destructive">{trace.error}</small>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminPanel>
    </>
  );
}
